import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import get from 'lodash/get';
import { last, startCase, toLower } from 'lodash';
import { MessageBar } from 'office-ui-fabric-react';
import { Text } from 'office-ui-fabric-react/lib/Text';
import { FontIcon } from 'office-ui-fabric-react/lib/Icon';
import Module from '../../components/Module';
import ModuleHeader from '../../components/ModuleHeader/ModuleHeader';
import { capitalizeWordsInString, formatDateString } from '../../utils';
import SearchBar from '../../components/SearchBar';
import ModuleContentList from '../../components/ModuleContentList';
import { fetchDimensionListDetails } from '../../actions/dimensionList';
import DimensionListsDetails from '../../components/DimensionListsDetails';
import styles from './DimensionListDetails.style';
import TextButton from '../../components/TextButton';
import saveExcel from '../../utils/excel';

const DimensionListDetails = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const { listId } = useParams();
  const { listType } = useParams();
  const [searchResults, setSearchResults] = useState([]);
  const getDimensionListDetails = id => {
    return useSelector(state =>
      get(state, `entities.dimensionListDetails.${id}`, { metadata: {} }),
    );
  };
  const dimensionListItemDetails = getDimensionListDetails(listId);

  useEffect(() => {
    dispatch(fetchDimensionListDetails(listId, { useCache: false }));
    setLoading(false);
  }, [dispatch, listId]);

  const handleClear = () => {
    setShowSearchResults(false);
    setLoading(false);
  };
  const filterDimensionListDetail = async query => {
    setLoading(true);
    if (listType === 'product') {
      const results = await dimensionListItemDetails.listItems.filter(
        item =>
          toLower(item.id).includes(query) ||
          toLower(item.name).includes(query) ||
          item.productIds.some(id => toLower(id).includes(query)) ||
          item.listHierarchy.some(listObj => {
            return Object.values(listObj).some(q => toLower(q).includes(query));
          }),
      );
      setSearchResults(results);
      return;
    }
    const results = await dimensionListItemDetails.listItems.filter(
      item =>
        toLower(item.id).includes(query) ||
        toLower(item.name).includes(query) ||
        item.listHierarchy.some(listObj => {
          return Object.values(listObj).some(q => toLower(q).includes(query));
        }),
    );
    setSearchResults(results);
  };

  const handleSearch = async query => {
    if (!query) {
      setSearchResults([]);
      setShowSearchResults(false);
      setLoading(false);
      return;
    }
    setShowSearchResults(true);
    await filterDimensionListDetail(toLower(query));
    setLoading(false);
  };

  // returns items for cell in excel
  const itemsForCell = items => {
    const itemsArray = [];
    items.forEach(item => {
      const itemObj = {};
      itemObj.name = item.name;
      if (listType === 'product') {
        itemObj.productIds = item.productIds?.join() || '';
      }
      itemObj.id = listType === 'product' && item.id ? parseFloat(item.id) : item.id;
      const listHierarchies = [];
      if (item.listHierarchy != null) {
        item.listHierarchy.forEach(listObj => {
          const hierarchyObj = Object.entries(listObj);
          if (hierarchyObj?.length > 2) {
            listHierarchies.push(`${last(hierarchyObj[1])} (Nested)`);
          } else {
            const hierarchy = Object.values(listObj);
            listHierarchies.push(last(hierarchy));
          }
        });
        itemObj.listHierarchy = Object.values(listHierarchies).join(',\r\n');
      } else {
        itemObj.listHierarchy = items.heirarchy;
      }
      itemsArray.push(itemObj);
    });
    return itemsArray;
  };

  const exportToExcel = () => {
    const dimensionItemsHeader = ['List Item', 'Token ID', 'Associated Lists'];
    const productItemsHeader = ['List Item', 'Item ID', 'Token ID', 'Associated Lists'];
    const sheetHeaders = [
      {
        cell: 'A1',
        value: 'List Name:',
        font: { bold: true, size: 16 },
        alignment: { horizontal: 'right' },
      },
      {
        cell: 'B1',
        value: dimensionListItemDetails.listName,
        font: { size: 16 },
        alignment: { horizontal: 'left' },
      },
      {
        cell: 'A2',
        value: 'List Type:',
        font: { bold: true, size: 16 },
        alignment: { horizontal: 'right' },
      },
      {
        cell: 'B2',
        value: dimensionListItemDetails.listType
          ? startCase(dimensionListItemDetails.listType)
          : '',
        font: { size: 16 },
        alignment: { horizontal: 'left' },
      },
      {
        cell: 'A3',
        value: 'Most Recent Revision Date:',
        font: { bold: true, size: 16 },
        alignment: { horizontal: 'right' },
      },
      {
        cell: 'B3',
        value: formatDateString(
          dimensionListItemDetails.metadata.lastUpdatedTimestamp,
          'MMM dd yyyy',
        ),
        font: { size: 16 },
        alignment: { horizontal: 'left' },
      },
    ];
    const columnsHeader = listType === 'product' ? productItemsHeader : dimensionItemsHeader;
    saveExcel(columnsHeader, itemsForCell(dimensionListItemDetails.listItems), sheetHeaders, {
      fileName: dimensionListItemDetails.listName,
      workSheetName: 'Dimension List',
    });
  };

  return (
    <>
      <div className={styles.searchSection}>
        <SearchBar
          placeholder="Search lists"
          ariaLabel="Search"
          underlined
          onChange={handleSearch}
          onClear={handleClear}
          data-testid="search-input"
          className={styles.search}
        />
      </div>
      {showSearchResults && (
        <ModuleHeader>
          <div className={styles.resultsMessage}>
            <Text as="h4" block>
              {searchResults.length} {searchResults.length === 1 ? 'Result' : 'Results'}
            </Text>
            {searchResults.length === 0 && (
              <Text as="p" role="alert" block className={styles.noResultsSentence}>
                {listType === 'product'
                  ? 'Try searching by product name or product Id'
                  : `Try searching by ${listType} name or ${listType} Id`}
              </Text>
            )}
          </div>
        </ModuleHeader>
      )}
      {!showSearchResults &&
        dimensionListItemDetails.listItems &&
        dimensionListItemDetails.listItems.length === 0 && (
          <MessageBar isMultiline className={styles.noDimensionDetail}>
            <Text>No {listType} lists or items</Text>
            <br />
            <Text className={styles.noItemDetails}>
              No {listType} lists or items have been added to this dimension list.
            </Text>
          </MessageBar>
        )}
      {!showSearchResults &&
        dimensionListItemDetails.listItems &&
        dimensionListItemDetails.listItems.length > 0 && (
          <div className={styles.buttonContainer}>
            <ModuleHeader>
              {dimensionListItemDetails.listItems.length}{' '}
              {listType ? capitalizeWordsInString(listType) : 'Product'} List Items
            </ModuleHeader>
            <TextButton className={styles.exportToExcel} onClick={exportToExcel}>
              <FontIcon
                iconName="DownloadDocument"
                arianame="DownloadDocument"
                className={styles.bulletedList}
              />
              Export to Excel
            </TextButton>
          </div>
        )}
      {showSearchResults && searchResults.length !== 0 && (
        <Module>
          <ModuleContentList>
            <DimensionListsDetails
              listName={dimensionListItemDetails.listName}
              shimmered
              enableShimmer={loading}
              dimensionItems={searchResults}
              listType={listType}
              className={styles.scrollablePane}
              multiLine
            />
          </ModuleContentList>
        </Module>
      )}
      {!showSearchResults && dimensionListItemDetails && dimensionListItemDetails.listItems && (
        <Module>
          <ModuleContentList>
            <DimensionListsDetails
              listName={dimensionListItemDetails.listName}
              dimensionItems={dimensionListItemDetails.listItems}
              listType={listType}
              shimmered
              enableShimmer={loading}
              className={styles.scrollablePane}
              multiLine
            />
          </ModuleContentList>
        </Module>
      )}
    </>
  );
};

export default DimensionListDetails;
